import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Save,
  Refresh,
  Email,
  Settings,
  NotificationsActive,
  NotificationsOff
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface NotificationRecipient {
  id: number;
  name: string;
  email: string;
  role?: string;
}

interface EmailSetting {
  id?: number;
  notification_type: string;
  recipient_user_ids: number[];
  is_active: boolean;
}

interface EmailSettingsData {
  settings: EmailSetting[];
  notification_types: { [key: string]: string };
  recipients: NotificationRecipient[];
  pmp_admins?: NotificationRecipient[];
}

const EmailSettings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<EmailSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-settings');
      setData(response.data.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      setSnackbar({ open: true, message: 'Failed to load email settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSetting = (notificationType: string): EmailSetting => {
    const existingSetting = data?.settings.find(s => s.notification_type === notificationType);
    return existingSetting || {
      notification_type: notificationType,
      recipient_user_ids: [],
      is_active: true
    };
  };

  const handleRecipientsChange = async (notificationType: string, recipientIds: number[]) => {
    if (!data) return;

    try {
      setSaving(notificationType);
      await api.put(`/email-settings/${notificationType}`, {
        recipient_user_ids: recipientIds,
        is_active: true
      });
      
      setSnackbar({ open: true, message: 'Email settings updated successfully', severity: 'success' });
      await fetchEmailSettings(); // Refresh data
    } catch (error: any) {
      console.error('Error updating email settings:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to update email settings', 
        severity: 'error' 
      });
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActive = async (notificationType: string, isActive: boolean) => {
    if (!data) return;

    try {
      setSaving(notificationType);
      const currentSetting = getCurrentSetting(notificationType);
      await api.put(`/email-settings/${notificationType}`, {
        recipient_user_ids: currentSetting.recipient_user_ids,
        is_active: isActive
      });
      
      setSnackbar({ open: true, message: 'Email settings updated successfully', severity: 'success' });
      await fetchEmailSettings(); // Refresh data
    } catch (error: any) {
      console.error('Error updating email settings:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to update email settings', 
        severity: 'error' 
      });
    } finally {
      setSaving(null);
    }
  };

  const handleResetToDefault = async (notificationType: string) => {
    try {
      setSaving(notificationType);
      await api.post(`/email-settings/${notificationType}/reset`);
      
      setSnackbar({ open: true, message: 'Email settings reset to default (all PMP and Site administrators)', severity: 'success' });
      await fetchEmailSettings(); // Refresh data
    } catch (error: any) {
      console.error('Error resetting email settings:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to reset email settings', 
        severity: 'error' 
      });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography>Loading email settings...</Typography>
        </Box>
      </Container>
    );
  }

  if (!isPmpAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. This section is only available to PMP Admins.
        </Alert>
      </Box>
    );
  }

  const getRecipients = (): NotificationRecipient[] => {
    if (!data) return [];
    return data.recipients?.length ? data.recipients : (data.pmp_admins || []);
  };

  const getRoleLabel = (role?: string) => {
    if (role === 'site_admin') return 'Site Admin';
    if (role === 'pmp_admin') return 'PMP Admin';
    return role?.replace(/_/g, ' ') || 'Admin';
  };

  if (!data) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography color="error">Failed to load email settings</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Email color="primary" />
            <Typography variant="h4">Email Notification Settings</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchEmailSettings}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" color="textSecondary">
            Configure which PMP administrators and Site administrators should receive different types of email notifications.
            By default, all active PMP and Site administrators receive all notifications.
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {Object.entries(data.notification_types).map(([notificationType, displayName]) => {
            const setting = getCurrentSetting(notificationType);
            const availableRecipients = getRecipients();
            const selectedRecipients = availableRecipients.filter((recipient) =>
              setting.recipient_user_ids.includes(recipient.id)
            );

            return (
              <Card key={notificationType} elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {displayName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Notification Type: {notificationType}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={setting.is_active}
                            onChange={(e) => handleToggleActive(notificationType, e.target.checked)}
                            disabled={saving === notificationType}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {setting.is_active ? <NotificationsActive color="success" /> : <NotificationsOff color="disabled" />}
                            {setting.is_active ? 'Active' : 'Inactive'}
                          </Box>
                        }
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2">
                      Recipients ({selectedRecipients.length} of {availableRecipients.length} administrators):
                    </Typography>

                    <FormControl fullWidth disabled={saving === notificationType || !setting.is_active}>
                      <InputLabel>Select Recipients</InputLabel>
                      <Select
                        multiple
                        value={setting.recipient_user_ids}
                        onChange={(e) => handleRecipientsChange(notificationType, e.target.value as number[])}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as number[]).map((recipientId) => {
                              const recipient = availableRecipients.find((r) => r.id === recipientId);
                              return recipient ? (
                                <Chip
                                  key={recipientId}
                                  label={`${recipient.name} (${getRoleLabel(recipient.role)})`}
                                  size="small"
                                  color="primary"
                                />
                              ) : null;
                            })}
                          </Box>
                        )}
                      >
                        {availableRecipients.map((recipient) => (
                          <MenuItem key={recipient.id} value={recipient.id}>
                            <Box>
                              <Typography variant="body2">
                                {recipient.name} — {getRoleLabel(recipient.role)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {recipient.email}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedRecipients.length === 0 && setting.is_active && (
                      <Alert severity="warning">
                        No recipients selected. This notification type will fall back to all active PMP and Site administrators.
                      </Alert>
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button
                      startIcon={<Refresh />}
                      onClick={() => handleResetToDefault(notificationType)}
                      disabled={saving === notificationType}
                    >
                      Reset to Default
                    </Button>
                    <Typography variant="caption" color="textSecondary" sx={{ alignSelf: 'center' }}>
                      {saving === notificationType ? 'Saving...' : 'Changes are saved automatically'}
                    </Typography>
                  </Box>
                </CardActions>
              </Card>
            );
          })}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EmailSettings;
